import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("ap_token"));
  const [user, setUser] = useState(null);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    if (res.data.requires_2fa) {
      return { requires_2fa: true };
    }
    const { access_token, refresh_token } = res.data;
    localStorage.setItem("ap_token", access_token);
    localStorage.setItem("ap_refresh", refresh_token);
    setToken(access_token);
    setUser(username);
    return true;
  }, []);

  const login2FA = useCallback(async (username, password, code) => {
    const res = await axios.post(`${API}/auth/2fa/verify`, { username, password, code });
    const { access_token, refresh_token } = res.data;
    localStorage.setItem("ap_token", access_token);
    localStorage.setItem("ap_refresh", refresh_token);
    setToken(access_token);
    setUser(username);
    return true;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ap_token");
    localStorage.removeItem("ap_refresh");
    setToken(null);
    setUser(null);
  }, []);

  const logoutAllDevices = useCallback(async () => {
    const t = localStorage.getItem("ap_token");
    if (t) {
      try {
        await axios.post(
          `${API}/auth/logout-all`,
          {},
          { headers: { Authorization: `Bearer ${t}` } }
        );
      } catch (e) {
        // even if request fails, still clear local session
      }
    }
    localStorage.removeItem("ap_token");
    localStorage.removeItem("ap_refresh");
    setToken(null);
    setUser(null);
  }, []);

  const authAxios = useCallback(() => {
    const t = localStorage.getItem("ap_token");
    const instance = axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${t}` },
    });
    // Global 401 handling — if token is invalid/expired, hard-redirect to /login
    // instead of spamming the console. Same instance handles every request from
    // every manager, including heartbeat polls.
    instance.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err?.response?.status === 401) {
          try {
            localStorage.removeItem("ap_token");
            localStorage.removeItem("ap_refresh");
          } catch { /* ignore storage errors */ }
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            window.location.replace("/login");
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, login, login2FA, logout, logoutAllDevices, authAxios, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
